import React, { useEffect, useState, useRef } from 'react';
import { lang } from '@umu/ukit-lang';
import { Iconfont, Popover } from '@umu/umu-design';
import { PopoverProps } from '@umu/umu-design/es/components/Popover/index';
import { useEventListener } from '@umu/umu-react-hooks';
import { If } from 'common/src/components';

interface IGuideData {
    text: string; // 与"dangerousHTMLContent"互斥
    btnText?: string; // return true 则不进入“下一步”
    closable?: boolean;
    customIndexText?: string;
    dangerousHTMLContent?: string; // 用于 dangerouslySetInnerHTML，与"text"互斥
    overlayClassName?: string;
    _overlay?: any;
    callback?(): boolean | void;
    onClose?: () => void;
}

type baseTrigger = 'hover' | 'focus' | 'click';

export interface IGuideProps extends PopoverProps {
    guideData: IGuideData | IGuideData[];
    index?: number;
    /**
     * 必须走到最后一步才能关闭气泡, 只对 trigger=active 有效
     */
    require?: boolean;
    /**
     * 定位基准，有的地方是 fixed 的，使 popover 不跟随页面滚动
     */
    root?: HTMLElement;
    children?: React.ReactElement;
    trigger?: baseTrigger | 'active';
    isWithoutFooter?: boolean;
}

import './guidePopover.scss';

export const GuidePopover = (props: IGuideProps) => {
    const {
        children,
        guideData,
        require,
        index = 1,
        placement = 'top',
        trigger = 'active',
        isWithoutFooter = false,
    } = props;
    const isActiveTrigger = trigger === 'active';
    const isStepGuides = Array.isArray(guideData);
    const contentRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(isActiveTrigger);
    const [currentIndex, setCurrentState] = useState(Math.max(index - 1, 0));
    const isLastStep = isStepGuides && currentIndex === (guideData as IGuideData[]).length - 1;
    const currentData = isStepGuides ? (guideData as IGuideData[])[currentIndex] : (guideData as IGuideData);
    const isDangerousContent = !!currentData.dangerousHTMLContent;

    const listenHandle = (e: Event) => {
        const content = contentRef.current;
        if (!content || !content.contains(e.target as Node)) {
            setVisible(false);
        }
    };

    useEventListener('click', listenHandle, !require && isActiveTrigger);

    useEffect(() => {
        setCurrentState(Math.max(index - 1, 0));
    }, [index]);

    const onNext = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        const cancelNext = currentData && currentData.callback && currentData.callback();
        if (cancelNext) {
            return;
        }
        if (!isStepGuides || isLastStep) {
            setVisible(false);
        } else {
            setCurrentState(currentIndex + 1);
        }
    };

    const renderChild = (): React.ReactNode => {
        return <div className={`guide-popover-child ${children ? '' : 'empty'}`}>{children}</div>;
    };

    const renderDangerousHTMLContent = (): React.ReactNode => {
        if (currentData.dangerousHTMLContent) {
            return (
                <div
                    className="guide-popover-content-dangerous-html"
                    dangerouslySetInnerHTML={{
                        __html: (guideData as IGuideData).dangerousHTMLContent || '',
                    }}
                />
            );
        }

        return null;
    };

    const renderCurrentIndex = () => {
        if (!isStepGuides) {
            if (currentData.customIndexText) {
                return currentData.customIndexText;
            }

            return null;
        }
        return `${currentIndex + 1}/${(guideData as IGuideData[]).length}`;
    };

    const renderCloseIcon = () => {
        const { onClose } = currentData;
        const handleClose = () => {
            setVisible(false);
            onClose instanceof Function && onClose();
        };

        return <Iconfont className="guide-popover-icon-close" type={'Close'} onClick={handleClose} />;
    };

    const renderContent = () => {
        if (!currentData) {
            return null;
        }

        const stepBtnText = lang('下一步');
        const defaultBtnText = lang('知道了');
        const btnText = currentData.btnText || (!isStepGuides || isLastStep ? defaultBtnText : stepBtnText);

        return (
            <div className="guide-popover-content" ref={contentRef}>
                <If condition={!!currentData.closable}>
                    <>{renderCloseIcon()}</>
                </If>
                <If condition={isDangerousContent}>
                    <>{renderDangerousHTMLContent()}</>
                </If>
                <If condition={!isDangerousContent}>
                    <div className="guide-popover-content-desc">{currentData.text}</div>
                </If>
                <If condition={!isWithoutFooter}>
                    <div className="guide-popover-content-footer">
                        <div className="guide-index">{renderCurrentIndex()}</div>
                        <button className="guide-btn" onClick={onNext}>
                            {btnText}
                        </button>
                    </div>
                </If>
            </div>
        );
    };

    const render = () => {
        const popoverTrigger = isActiveTrigger ? 'click' : trigger;
        const popoverProps: any = {
            color: '#1e6ee6',
            placement: placement,
            trigger: popoverTrigger as baseTrigger,
            overlayClassName: currentData.overlayClassName,
            _overlay: currentData._overlay,
            content: renderContent,
        };

        if (isActiveTrigger) {
            popoverProps.visible = visible;
        }

        if (props.root) {
            popoverProps.getPopupContainer = () => {
                return props.root;
            };
        }

        return <Popover {...popoverProps}>{renderChild()}</Popover>;
    };

    return render();
};
